import { useState, useEffect, useRef, useCallback } from 'react';

interface UseInactivityTimerOptions {
  timeout: number; // Timeout in milliseconds (default: 60000 = 1 minute)
  onInactive: () => void;
  onActive?: () => void;
  events?: string[]; // Events to listen to (default: mouse, keyboard, touch)
  enabled?: boolean; // Whether the timer is enabled (default: true)
}

export const useInactivityTimer = ({
  timeout = 60000, // 1 minute default
  onInactive,
  onActive,
  events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'],
  enabled = true,
}: UseInactivityTimerOptions) => {
  const [isActive, setIsActive] = useState(true);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  const resetTimer = useCallback(() => {
    if (!enabled) return; // Don't reset if timer is disabled
    
    try {
      const wasInactive = !isActive;
      
      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Update last activity time
      lastActivityRef.current = Date.now();

      // If was inactive, call onActive callback
      if (wasInactive && onActive) {
        try {
          onActive();
        } catch (error) {
          // Silently handle errors from callbacks (e.g., browser extension interference)
          console.debug('Activity callback error (ignored):', error);
        }
      }

      setIsActive(true);

      // Set new timeout
      timeoutRef.current = setTimeout(() => {
        try {
          setIsActive(false);
          onInactive();
        } catch (error) {
          // Silently handle errors from callbacks
          console.debug('Inactivity callback error (ignored):', error);
        }
      }, timeout);
    } catch (error) {
      // Silently handle any errors in timer reset
      console.debug('Timer reset error (ignored):', error);
    }
  }, [timeout, onInactive, onActive, isActive, enabled]);

  useEffect(() => {
    if (!enabled) {
      // Clear timeout if disabled
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    try {
      // Set initial timeout
      resetTimer();

      // Add event listeners with error handling
      const wrappedResetTimer = (e: Event) => {
        try {
          resetTimer();
        } catch (error) {
          // Silently handle errors (e.g., from browser extensions)
          console.debug('Event listener error (ignored):', error);
        }
      };

      events.forEach((event) => {
        try {
          window.addEventListener(event, wrappedResetTimer, { passive: true, capture: false });
        } catch (error) {
          // Silently handle errors adding listeners
          console.debug(`Failed to add listener for ${event} (ignored):`, error);
        }
      });

      // Cleanup
      return () => {
        try {
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
          events.forEach((event) => {
            try {
              window.removeEventListener(event, wrappedResetTimer);
            } catch (error) {
              // Silently handle errors removing listeners
              console.debug(`Failed to remove listener for ${event} (ignored):`, error);
            }
          });
        } catch (error) {
          console.debug('Cleanup error (ignored):', error);
        }
      };
    } catch (error) {
      console.debug('useEffect setup error (ignored):', error);
    }
  }, [resetTimer, events, enabled]);

  const reset = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  return {
    isActive,
    reset,
    lastActivity: lastActivityRef.current,
  };
};

